import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import styles from './WordStudy.module.css';
import { LoaderIcon, HeartIcon, SettingsIcon, ArrowLeftIcon } from '../components/Icons';
import WordModal from '../components/chapter/WordModal/WordModal';

const PAUSE_OPTIONS = [0, 1, 2];
const REPEAT_OPTIONS = [1, 2, 3, 4, 5];
const AVERAGE_PRONUNCIATION_MS = 1800;

const WordStudy = () => {
  const { bookId, chapterId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [words, setWords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [favoriteLoading, setFavoriteLoading] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [autoPlayEnabled, setAutoPlayEnabled] = useState(true);
  const [autoAdvanceEnabled, setAutoAdvanceEnabled] = useState(false);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [pauseSeconds, setPauseSeconds] = useState(PAUSE_OPTIONS[0]);
  const [repeatTimes, setRepeatTimes] = useState(REPEAT_OPTIONS[0]);
  const [showWordModal, setShowWordModal] = useState(false);
  const [activeDict, setActiveDict] = useState('youdao');
  const [dictLoading, setDictLoading] = useState({});
  const [dictResults, setDictResults] = useState({});
  const [hideSpelling, setHideSpelling] = useState(false);
  const [hidePhonetics, setHidePhonetics] = useState(false);
  const [hideDefinitions, setHideDefinitions] = useState(false);
  const SETTINGS_STORAGE_KEY = 'wordStudySettings';
  const audioRef = useRef(null);
  const suppressAutoPlayRef = useRef(false);
  const autoPlayInitRef = useRef(false);
  const containerRef = useRef(null);
  const initialStateRef = useRef(location.state);
  const [showCompletion, setShowCompletion] = useState(false);
  const [spellingInput, setSpellingInput] = useState('');
  const [spellingStatus, setSpellingStatus] = useState('idle'); // idle | success | error
  const spellingInputRef = useRef(null);
  const spellingSuccessTimerRef = useRef(null);
  
  useEffect(() => {
    if (location.state) {
      initialStateRef.current = location.state;
    } else if (!initialStateRef.current && chapterId) {
      initialStateRef.current = {
        from: 'chapterLearn',
        fromChapterId: Number(chapterId),
        backTo: {
          pathname: `/learn/${bookId}/${chapterId}/words`,
          search: '',
          hash: '',
        },
      };
    }
  }, [location.state, chapterId, bookId]);

  const updateIndexInUrl = useCallback((index) => {
    const search = Number.isFinite(index) && index >= 0 ? `?index=${index}` : '';
    navigate({ pathname: location.pathname, search }, { replace: true, state: initialStateRef.current });
  }, [navigate, location.pathname]);
  const currentWord = words[currentIndex];

  useEffect(() => {
    console.log('[WordStudy] state snapshot', {
      wordsLength: words.length,
      currentIndex,
      showCompletion,
    });
  }, [words.length, currentIndex, showCompletion]);

  // 从URL获取当前索引
  useEffect(() => {
    const indexParam = searchParams.get('index');
    if (indexParam) {
      const index = parseInt(indexParam, 10);
      if (!isNaN(index) && index >= 0) {
        setCurrentIndex(index);
      }
    }
  }, [searchParams]);

  // 读取本地设置
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      const raw = window.localStorage.getItem(SETTINGS_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (typeof parsed.autoPlay === 'boolean') {
          setAutoPlayEnabled(parsed.autoPlay);
        }
        if (typeof parsed.autoAdvance === 'boolean') {
          setAutoAdvanceEnabled(parsed.autoAdvance);
        }
        if (typeof parsed.pauseSeconds === 'number' && PAUSE_OPTIONS.includes(parsed.pauseSeconds)) {
          setPauseSeconds(parsed.pauseSeconds);
        }
        if (typeof parsed.repeatTimes === 'number' && REPEAT_OPTIONS.includes(parsed.repeatTimes)) {
          setRepeatTimes(parsed.repeatTimes);
        }
        if (typeof parsed.hideSpelling === 'boolean') {
          setHideSpelling(parsed.hideSpelling);
        }
        if (typeof parsed.hidePhonetics === 'boolean') {
          setHidePhonetics(parsed.hidePhonetics);
        }
        if (typeof parsed.hideDefinitions === 'boolean') {
          setHideDefinitions(parsed.hideDefinitions);
        }
      }
    } catch (error) {
      console.warn('读取单词学习设置失败:', error);
    } finally {
      setSettingsLoaded(true);
    }
  }, []);

  // 获取单词列表
  useEffect(() => {
    const fetchWords = async () => {
      try {
        setLoading(true);
        setError('');
        const response = await api.get(`/favorite-words/by-book/${bookId}`);
        const wordsData = Array.isArray(response.data) ? response.data : [];
        setWords(wordsData);
        
        if (wordsData.length === 0) {
          setError('暂无收藏单词');
        }
      } catch (err) {
        console.error('加载单词失败:', err);
        setError('加载单词失败，请稍后再试');
      } finally {
        setLoading(false);
      }
    };

    if (bookId) {
      fetchWords();
    }
  }, [bookId]);

  // 自动播放当前单词发音
  useEffect(() => {
    if (!autoPlayInitRef.current) {
      suppressAutoPlayRef.current = false;
      if (typeof window !== 'undefined') {
        const forcePlay = window.sessionStorage.getItem('wordStudyForcePlay');
        if (forcePlay === 'true') {
          window.sessionStorage.removeItem('wordStudyForcePlay');
        }
      }
      autoPlayInitRef.current = true;
    }

    if (!autoPlayEnabled) {
      return undefined;
    }

    if (words.length > 0) {
      const safeIndex = autoPlayInitRef.current ? currentIndex : 0;
      const currentWord = words[safeIndex];
      if (currentWord?.word) {
        // 延迟500ms播放发音，让页面先渲染
        const timer = setTimeout(() => {
          suppressAutoPlayRef.current = true;
          playCurrentWord(safeIndex);
          suppressAutoPlayRef.current = false;
        }, 500);
        return () => clearTimeout(timer);
      }
    }

    return undefined;
  }, [autoPlayEnabled, currentIndex, words]);

  const wordsLength = words.length;
  const shouldAutoAdvance = autoAdvanceEnabled;
  const touchStartRef = useRef({ x: 0, y: 0, active: false });

  const createPlaybackQueue = useCallback(() => {
    if (!autoPlayEnabled || words.length === 0) {
      return [];
    }

    return words.map((word, index) => ({
      index,
      repeatTimes,
      pauseMs: pauseSeconds * 1000,
    }));
  }, [autoPlayEnabled, pauseSeconds, repeatTimes, words]);

  const stopActiveAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
  };

  const playPronunciation = (word, type = 0) => {
    const cleanWord = word?.trim();
    if (!cleanWord) return;
    
    stopActiveAudio();

    return new Promise((resolve) => {
      try {
        const audio = new Audio(
          `http://dict.youdao.com/dictvoice?type=${type}&audio=${encodeURIComponent(cleanWord)}`
        );
        audioRef.current = audio;

        const cleanup = () => {
          if (audioRef.current === audio) {
            audioRef.current = null;
          }
          audio.removeEventListener('ended', cleanup);
          audio.removeEventListener('error', cleanup);
          resolve(true);
        };

        audio.addEventListener('ended', cleanup);
        audio.addEventListener('error', cleanup);

        audio.play().catch((err) => {
          console.error('播放发音失败:', err);
          cleanup();
        });
      } catch (error) {
        console.error('播放发音失败:', error);
        resolve(false);
      }
    });
  };

  const playCurrentWord = async (index) => {
    if (!autoPlayEnabled) {
      return;
    }

    const targetWord = words[index];
    if (!targetWord?.word) {
      return;
    }

    for (let i = 0; i < repeatTimes; i += 1) {
      const success = await playPronunciation(targetWord.word, 0);
      if (i < repeatTimes - 1) {
        await new Promise((resolve) => setTimeout(resolve, 900));
      }
      if (!success) {
        break;
      }
    }
  };

  // 自动跳转到下一个单词
  useEffect(() => {
    if (!shouldAutoAdvance || wordsLength === 0) {
      return undefined;
    }

    if (currentIndex >= wordsLength - 1) {
      return undefined;
    }

    const averageRepeatDuration = repeatTimes * AVERAGE_PRONUNCIATION_MS + Math.max(0, repeatTimes - 1) * 900;
    const delay = autoPlayEnabled
      ? averageRepeatDuration + pauseSeconds * 1000
      : (pauseSeconds > 0 ? pauseSeconds * 1000 : 3000);
    const timer = setTimeout(() => {
      const nextIndex = currentIndex + 1;
      if (nextIndex >= wordsLength) {
        return;
      }

      setCurrentIndex(nextIndex);
      updateIndexInUrl(nextIndex);

      if (autoPlayEnabled) {
        // 稍作延迟，确保页面渲染完成后再播放
        setTimeout(() => {
          playCurrentWord(nextIndex);
        }, 250);
      }
    }, delay);

    return () => clearTimeout(timer);
  }, [autoAdvanceEnabled, autoPlayEnabled, currentIndex, pauseSeconds, repeatTimes, shouldAutoAdvance, words, wordsLength, updateIndexInUrl]);

  const handleNext = () => {
    if (!words.length) {
      return;
    }

    if (showCompletion) {
      setShowCompletion(false);
      setCurrentIndex(0);
      updateIndexInUrl(0);
      playCurrentWord(0);
      return;
    }

    if (currentIndex >= words.length - 1) {
      setShowCompletion(true);
      return;
    }

    const nextIndex = currentIndex + 1;
    setCurrentIndex(nextIndex);
    updateIndexInUrl(nextIndex);
    playCurrentWord(nextIndex);
  };

  const handlePrevious = () => {
    if (!words.length) {
      return;
    }

    if (showCompletion) {
      setShowCompletion(false);
      return;
    }

    if (currentIndex > 0) {
      const newIndex = currentIndex - 1;
      setCurrentIndex(newIndex);
      updateIndexInUrl(newIndex);
      playCurrentWord(newIndex);
    } else {
      const newIndex = words.length - 1;
      setCurrentIndex(newIndex);
      updateIndexInUrl(newIndex);
      playCurrentWord(newIndex);
    }
  };

  const handleContainerClick = (event) => {
    if (showCompletion) {
      setShowCompletion(false);
      if (words.length) {
        setCurrentIndex(0);
        updateIndexInUrl(0);
        playCurrentWord(0);
      }
      return;
    }

    if (showWordModal) {
      return;
    }

    if (event.defaultPrevented) {
      return;
    }

    const interactiveTarget = event.target.closest(
      'button, a, input, textarea, select, label, [data-prevent-nav="true"]'
    );
    if (interactiveTarget) {
      return;
    }

    if (!containerRef.current) {
      return;
    }

    const rect = containerRef.current.getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    if (Number.isNaN(clickX) || rect.width === 0) {
      return;
    }

    if (clickX < rect.width / 2) {
      handlePrevious();
    } else {
      handleNext();
    }
  };

  const SWIPE_THRESHOLD = 60;
  const MAX_VERTICAL_DELTA = 80;

  const handleTouchStart = (event) => {
    if (event.touches.length !== 1) {
      return;
    }
    const touch = event.touches[0];
    touchStartRef.current = {
      x: touch.clientX,
      y: touch.clientY,
      active: true,
    };
  };

  const handleTouchMove = (event) => {
    if (!touchStartRef.current.active || event.touches.length !== 1) {
      return;
    }
    const touch = event.touches[0];
    const deltaY = Math.abs(touch.clientY - touchStartRef.current.y);
    if (deltaY > MAX_VERTICAL_DELTA) {
      touchStartRef.current.active = false;
    }
  };

  const handleTouchEnd = (event) => {
    if (!touchStartRef.current.active) {
      touchStartRef.current = { x: 0, y: 0, active: false };
      return;
    }
    const touch = event.changedTouches?.[0];
    if (!touch) {
      touchStartRef.current = { x: 0, y: 0, active: false };
      return;
    }

    const deltaX = touch.clientX - touchStartRef.current.x;
    const deltaY = Math.abs(touch.clientY - touchStartRef.current.y);
    touchStartRef.current = { x: 0, y: 0, active: false };

    if (deltaY > MAX_VERTICAL_DELTA) {
      return;
    }

    if (deltaX <= -SWIPE_THRESHOLD) {
      handleNext();
    } else if (deltaX >= SWIPE_THRESHOLD) {
      handlePrevious();
    }
  };

  const handleBack = () => {
    if (location.state?.from === 'history') {
      const historyReturn = location.state?.historyReturn;
      if (historyReturn) {
        navigate('/history', { state: { historyView: historyReturn } });
      } else {
        navigate('/history');
      }
      return;
    }

    const backTo = location.state?.backTo;
    if (backTo && typeof backTo === 'object' && backTo.pathname) {
      const targetPath = `${backTo.pathname}${backTo.search || ''}${backTo.hash || ''}`;
      if (backTo.state !== undefined) {
        navigate(targetPath, { state: backTo.state });
      } else {
        navigate(targetPath);
      }
      return;
    }

    const rawFromChapterId = location.state?.fromChapterId;
    const fromChapterId = typeof rawFromChapterId === 'number' && Number.isFinite(rawFromChapterId)
      ? rawFromChapterId
      : undefined;

    if (bookId && fromChapterId !== undefined && location.state?.from === 'chapterLearn') {
      navigate(`/learn/${bookId}/${fromChapterId}/words`);
      return;
    }

    if (bookId) {
      navigate(`/book/${bookId}`);
      return;
    }

    navigate(-1);
  };

  const handleToggleSettings = () => {
    setShowSettings((prev) => !prev);
  };

  const handleToggleDefinition = () => {
    if (!currentWord?.word) {
      return;
    }
    setShowWordModal(true);
    setActiveDict('youdao');
    // 初始化 youdao 字典结果（从本地数据）
    const wordData = {
      word: currentWord.word,
      success: true,
      youdao_definition: currentWord.word_jsondata?.youdao_definition || currentWord.word_jsondata,
      phonetic: currentWord.word_jsondata?.phonetic,
    };
    setDictResults({
      youdao: {
        success: true,
        data: {
          definition: wordData.youdao_definition,
        },
      },
    });
  };

  const handleDictTabClick = async (dictName) => {
    setActiveDict(dictName);
    
    if (dictName === 'mnemonic') {
      return;
    }

    // 如果已经加载过该字典，直接返回
    if (dictResults[dictName]) {
      return;
    }

    // 查询字典
    setDictLoading((prev) => ({ ...prev, [dictName]: true }));
    
    try {
      const response = await api.get(`/dict/${dictName}/${currentWord.word}`);
      setDictResults((prev) => ({
        ...prev,
        [dictName]: response.data,
      }));
    } catch (error) {
      console.error(`查询${dictName}字典失败:`, error);
      setDictResults((prev) => ({
        ...prev,
        [dictName]: {
          success: false,
          error: error.message,
        },
      }));
    } finally {
      setDictLoading((prev) => ({ ...prev, [dictName]: false }));
    }
  };

  const handlePlayPronunciation = (type = 0) => {
    if (currentWord?.word) {
      playPronunciation(currentWord.word, type);
    }
  };

  const handleCloseModal = () => {
    setShowWordModal(false);
    setDictResults({});
    setDictLoading({});
  };

  const handleToggleAutoplay = () => {
    setAutoPlayEnabled((prev) => !prev);
  };

  const handleToggleAutoAdvance = () => {
    setAutoAdvanceEnabled((prev) => !prev);
  };

  const handleToggleHideSpelling = () => {
    setHideSpelling((prev) => !prev);
  };

  const handleToggleHidePhonetics = () => {
    setHidePhonetics((prev) => !prev);
  };

  const handleToggleHideDefinitions = () => {
    setHideDefinitions((prev) => !prev);
  };

  const handlePauseChange = (event) => {
    const value = Number(event.target.value);
    if (PAUSE_OPTIONS.includes(value)) {
      setPauseSeconds(value);
    }
  };

  const handleRepeatChange = (event) => {
    const value = Number(event.target.value);
    if (REPEAT_OPTIONS.includes(value)) {
      setRepeatTimes(value);
    }
  };

  // 持久化用户设置
  useEffect(() => {
    if (typeof window === 'undefined' || !settingsLoaded) {
      return;
    }

    try {
      const payload = {
        autoPlay: autoPlayEnabled,
        autoAdvance: autoAdvanceEnabled,
        pauseSeconds,
        repeatTimes,
        hideSpelling,
        hidePhonetics,
        hideDefinitions,
      };
      window.localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(payload));
    } catch (error) {
      console.warn('保存单词学习设置失败:', error);
    }
  }, [
    autoAdvanceEnabled,
    autoPlayEnabled,
    hideDefinitions,
    hidePhonetics,
    hideSpelling,
    pauseSeconds,
    repeatTimes,
    settingsLoaded,
  ]);

  useEffect(() => () => {
    stopActiveAudio();
    if (spellingSuccessTimerRef.current) {
      clearTimeout(spellingSuccessTimerRef.current);
      spellingSuccessTimerRef.current = null;
    }
  }, []);

  const handleUnfavorite = async () => {
    const targetWord = currentWord;
    if (!targetWord?.id) return;

    setFavoriteLoading(true);
    try {
      await api.delete(`/favorite-words/${targetWord.id}`);
      
      // 关闭弹窗
      setShowWordModal(false);
      
      // 从列表中移除该单词
      const newWords = words.filter((_, idx) => idx !== currentIndex);
      setWords(newWords);
      
      // 如果还有单词，调整当前索引
      if (newWords.length > 0) {
        const newIndex = Math.min(currentIndex, newWords.length - 1);
        setCurrentIndex(newIndex);
        updateIndexInUrl(newIndex);
      } else {
        // 没有单词了，返回上一页
        handleBack();
      }
    } catch (error) {
      console.error('取消收藏失败:', error);
      alert('取消收藏失败，请稍后重试');
    } finally {
      setFavoriteLoading(false);
    }
  };

  const buildPhoneticEntries = (wordData) => {
    const entries = [];
    const youdaoData = wordData?.youdao_definition || wordData;
    
    if (youdaoData?.phonetics) {
      if (youdaoData.phonetics.us) {
        entries.push({ label: '美', value: youdaoData.phonetics.us, type: 0 });
      }
      if (youdaoData.phonetics.uk) {
        entries.push({ label: '英', value: youdaoData.phonetics.uk, type: 1 });
      }
    }

    if (entries.length === 0 && wordData?.phonetic) {
      entries.push({ label: '', value: wordData.phonetic, type: 0 });
    }

    return entries;
  };

  const wordData = currentWord?.word_jsondata || {};
  const phoneticEntries = buildPhoneticEntries(wordData);
  const basicDefinitions = useMemo(() => {
    const youdaoDefinition = (wordData && typeof wordData === 'object')
      ? (wordData.youdao_definition || wordData)
      : null;
    if (!youdaoDefinition || typeof youdaoDefinition !== 'object') {
      return [];
    }

    const collectStrings = (value) => {
      if (!value) return [];
      if (Array.isArray(value)) {
        return value
          .map((item) => (typeof item === 'string' ? item.trim() : ''))
          .filter(Boolean);
      }
      if (typeof value === 'string') {
        const trimmed = value.trim();
        return trimmed ? [trimmed] : [];
      }
      return [];
    };

    const simpleDefs = collectStrings(youdaoDefinition?.definitions?.simple);
    if (simpleDefs.length > 0) {
      return simpleDefs;
    }

    const translations = collectStrings(youdaoDefinition?.translation);
    if (translations.length > 0) {
      return translations;
    }

    return [];
  }, [wordData]);
  const normalizedTargetWord = (currentWord?.word || '').trim();

  const normalizedValue = useCallback((value) => value.replace(/\s+/g, '').toLowerCase(), []);

  const handleSpellingSuccess = useCallback(() => {
    setSpellingStatus('success');
    setSpellingInput(normalizedTargetWord);
    if (spellingSuccessTimerRef.current) {
      clearTimeout(spellingSuccessTimerRef.current);
    }
    spellingSuccessTimerRef.current = setTimeout(() => {
      spellingSuccessTimerRef.current = null;
      handleNext();
    }, 600);
  }, [handleNext, normalizedTargetWord]);

  const handleSpellingChange = useCallback((event) => {
    const rawValue = event.target.value;
    const sanitized = rawValue.replace(/\s+/g, '');
    const clipped = normalizedTargetWord ? sanitized.slice(0, normalizedTargetWord.length) : sanitized;
    setSpellingInput(clipped);
    if (spellingStatus === 'error') {
      setSpellingStatus('idle');
    }
    if (!normalizedTargetWord) {
      return;
    }
    if (normalizedValue(clipped) === normalizedValue(normalizedTargetWord)) {
      handleSpellingSuccess();
    } else if (spellingStatus === 'success') {
      setSpellingStatus('idle');
    }
  }, [handleSpellingSuccess, normalizedTargetWord, normalizedValue, spellingStatus]);

  const handleSpellingSubmit = useCallback((event) => {
    event.preventDefault();
    if (!normalizedTargetWord) {
      return;
    }
    if (normalizedValue(spellingInput) === normalizedValue(normalizedTargetWord)) {
      handleSpellingSuccess();
    } else {
      setSpellingStatus('error');
    }
  }, [handleSpellingSuccess, normalizedTargetWord, normalizedValue, spellingInput]);

  const spellingHelperMessage = useMemo(() => {
    if (spellingStatus === 'success') {
      return '拼写正确！即将进入下一个单词...';
    }
    if (spellingStatus === 'error') {
      return '拼写不正确，请再试一次。';
    }
    return '输入完整单词进行拼写记忆，正确后将自动跳转。';
  }, [spellingStatus]);

  useEffect(() => {
    setSpellingInput('');
    setSpellingStatus('idle');
    if (spellingSuccessTimerRef.current) {
      clearTimeout(spellingSuccessTimerRef.current);
      spellingSuccessTimerRef.current = null;
    }
    if (spellingInputRef.current) {
      spellingInputRef.current.focus({ preventScroll: true });
    }
  }, [currentIndex, words]);

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          <LoaderIcon size={32} />
          <p>加载中...</p>
        </div>
      </div>
    );
  }

  if (error || words.length === 0) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>
          <p>{error || '暂无单词'}</p>
          <button onClick={handleBack} type="button" className={styles.backBtn} aria-label="返回收藏单词列表">
            ←
          </button>
        </div>
      </div>
    );
  }

  if (showCompletion) {
    if (words.length) {
      setShowCompletion(false);
      setCurrentIndex(0);
      updateIndexInUrl(0);
      playCurrentWord(0);
      return null;
    }
  }

  return (
    <div
      ref={containerRef}
      className={styles.container}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchEnd}
      onClick={handleContainerClick}
    >
      {/* 头部导航 */}
      <div className={styles.header}>
        <button onClick={handleBack} type="button" className={styles.backBtn} aria-label="返回收藏单词列表">
        <ArrowLeftIcon size={20} />

        </button>
        <div className={styles.headerRight}>
          <div className={styles.progress}>
            {currentIndex + 1} / {words.length}
          </div>
          <button
            type="button"
            className={styles.settingsBtn}
            onClick={handleToggleSettings}
            aria-label="学习设置"
            aria-expanded={showSettings}
          >
            <SettingsIcon size={20} />
          </button>
        </div>
      </div>

      {showSettings && (
        <div className={styles.settingsPanel} data-prevent-nav="true">
          <div className={styles.settingsRow}>
            <span>自动播放发音</span>
            <label className={styles.toggleSwitch}>
              <input
                type="checkbox"
                checked={autoPlayEnabled}
                onChange={handleToggleAutoplay}
              />
              <span className={styles.slider} />
            </label>
          </div>
          <div className={styles.settingsRow}>
            <span>自动播放下一个</span>
            <label className={styles.toggleSwitch}>
              <input
                type="checkbox"
                checked={autoAdvanceEnabled}
                onChange={handleToggleAutoAdvance}
              />
              <span className={styles.slider} />
            </label>
          </div>
          <div className={styles.settingsRow}>
            <span>单词间停顿</span>
            <select
              className={styles.select}
              value={pauseSeconds}
              onChange={handlePauseChange}
            >
              {PAUSE_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option} 秒
                </option>
              ))}
            </select>
          </div>
          <div className={styles.settingsRow}>
            <span>重复次数</span>
            <select
              className={styles.select}
              value={repeatTimes}
              onChange={handleRepeatChange}
            >
              {REPEAT_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option} 次
                </option>
              ))}
            </select>
          </div>
          <div className={styles.settingsRow}>
            <span>隐藏拼写</span>
            <label className={styles.toggleSwitch}>
              <input
                type="checkbox"
                checked={hideSpelling}
                onChange={handleToggleHideSpelling}
              />
              <span className={styles.slider} />
            </label>
          </div>
          <div className={styles.settingsRow}>
            <span>隐藏音标</span>
            <label className={styles.toggleSwitch}>
              <input
                type="checkbox"
                checked={hidePhonetics}
                onChange={handleToggleHidePhonetics}
              />
              <span className={styles.slider} />
            </label>
          </div>
          <div className={styles.settingsRow}>
            <span>隐藏释义</span>
            <label className={styles.toggleSwitch}>
              <input
                type="checkbox"
                checked={hideDefinitions}
                onChange={handleToggleHideDefinitions}
              />
              <span className={styles.slider} />
            </label>
          </div>
        </div>
      )}

      {/* 单词卡片 */}
      <div className={styles.wordCard}>
        {!hideSpelling && (
          <div className={styles.cardHeader}>
            <div className={styles.wordTitle}>
              <h1>{currentWord.word}</h1>
            </div>
            <button
              onClick={handleUnfavorite}
              disabled={favoriteLoading}
              className={styles.unfavoriteBtn}
              title="取消收藏"
              aria-label="取消收藏"
            >
              {favoriteLoading ? (
                <LoaderIcon size={16} />
              ) : (
                <HeartIcon size={22} filled />
              )}
            </button>
          </div>
        )}

        {/* 音标和发音 */}
        {!hidePhonetics && phoneticEntries.length > 0 && (
          <div className={styles.phonetics}>
            {phoneticEntries.map((entry, index) => (
              <button
                key={index}
                className={styles.phoneticBtn}
                onClick={() => playPronunciation(currentWord.word, entry.type)}
              >
                {entry.label && <span className={styles.phoneticLabel}>{entry.label}</span>}
                <span className={styles.phoneticValue}>{entry.value}</span>
                <svg className={styles.speakerIcon} viewBox="0 0 16 16">
                  <path d="M3 6.5v3h2.5l3 2V4.5l-3 2H3z" fill="currentColor" />
                  <path
                    d="M10.5 5.25a2.75 2.75 0 010 5.5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.1"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            ))}
          </div>
        )}

        {!hideDefinitions && basicDefinitions.length > 0 && (
          <div className={styles.basicDefinitions}>
            <div className={styles.basicDefinitionsTitle}>基础释义</div>
            <ul className={styles.basicDefinitionList}>
              {basicDefinitions.map((definition, index) => (
                <li key={index} className={styles.basicDefinitionItem}>
                  {definition}
                </li>
              ))}
            </ul>
          </div>
        )}
 {/* 拼写记忆 */}
 <div className={styles.spellingSection} data-prevent-nav="true">
          <div className={styles.spellingHeader}>拼写记忆</div>
          <form className={styles.spellingForm} onSubmit={handleSpellingSubmit} data-prevent-nav="true">
            <div
              className={styles.spellingBoxesWrapper}
              onClick={() => spellingInputRef.current?.focus({ preventScroll: true })}
              role="presentation"
            >
              {Array.from({ length: Math.max(normalizedTargetWord.length, 1) }).map((_, index) => {
                const char = spellingInput[index] || '';
                const expectedChar = normalizedTargetWord[index] || '';
                const isFilled = !!char;
                const isCorrectChar = isFilled && char.toLowerCase() === expectedChar.toLowerCase();
                let boxClass = styles.spellingBox;
                if (spellingStatus === 'success') {
                  boxClass = `${boxClass} ${styles.spellingBoxSuccess}`;
                } else if (isFilled) {
                  boxClass = `${boxClass} ${isCorrectChar ? styles.spellingBoxFilled : styles.spellingBoxError}`;
                }
                return (
                  <span key={index} className={boxClass}>
                    {char}
                  </span>
                );
              })}
            </div>
            <input
              ref={spellingInputRef}
              type="text"
              className={styles.spellingHiddenInput}
              value={spellingInput}
              onChange={handleSpellingChange}
              autoComplete="off"
              spellCheck={false}
              data-prevent-nav="true"
              maxLength={normalizedTargetWord.length || undefined}
            />
          </form>
          <div
            className={`${styles.spellingHint} ${
              spellingStatus === 'success'
                ? styles.spellingHintSuccess
                : spellingStatus === 'error'
                  ? styles.spellingHintError
                  : ''
            }`}
          >
            {spellingHelperMessage}
          </div>
        </div>
        {/* 释义 */}
        <div className={styles.definitions}>
          <button
            type="button"
            className={styles.definitionToggleBtn}
            onClick={handleToggleDefinition}
          >
            详细释义
          </button>
         
        </div>

       
      </div>

      {/* 单词释义弹窗 */}
      <WordModal
        isOpen={showWordModal}
        wordLookup={currentWord ? {
          word: currentWord.word,
          success: true,
          youdao_definition: currentWord.word_jsondata?.youdao_definition || currentWord.word_jsondata,
          phonetic: currentWord.word_jsondata?.phonetic,
        } : null}
        activeDict={activeDict}
        dictLoading={dictLoading}
        dictResults={dictResults}
        favoriteLoading={favoriteLoading}
        isFavorited={true}
        onPlayPronunciation={handlePlayPronunciation}
        onFavoriteWord={handleUnfavorite}
        onDictTabClick={handleDictTabClick}
        onClose={handleCloseModal}
        className="word-study-modal"
      />
    </div>
  );
};

export default WordStudy;
